import { useMutation, useQuery } from '@apollo/client';

import { cacheUtils } from '@eluve/apollo-client';
import { MedicineDetails } from '@eluve/blocks';
import {
  H4,
  HStack,
  Icon,
  VStack,
  legacyTextStyles,
  useToast,
} from '@eluve/components';
import { useAppointmentActivity } from '@eluve/frontend-appointment-hooks';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';
import { InlineFeedbackBox } from '@eluve/smart-blocks';

import {
  addAppointmentMedicineMutation,
  appointmentMedicinesFragment,
  getAppointmentMedicationsQuery,
  removeAppointmentMedicineMutation,
} from './AppointmentMedicine.operations';
import { Medication, SearchableMedicines } from './SearchableMedicines';

export interface MedicineRecommendationProps {
  tenantId: string;
  appointmentId: string;
  isReadonly?: boolean;
}

export const MedicineRecommendations: React.FC<MedicineRecommendationProps> = ({
  tenantId,
  appointmentId,
  isReadonly,
}) => {
  const toast = useToast();
  const useFdaDrugs = useIsPostHogFeatureFlagEnabled('USE_FDA_DRUGS');
  const { appointmentLlmJobs } = useAppointmentActivity();
  const { jobStatus } =
    appointmentLlmJobs.find(
      (job) => job.llmOutputType === 'MEDICINE_RECOMMENDATION',
    ) ?? {};

  const { data } = useQuery(getAppointmentMedicationsQuery, {
    variables: {
      tenantId,
      appointmentId,
    },
    skip: !useFdaDrugs && jobStatus !== 'COMPLETED',
  });
  const selectedMedicines = data?.appointmentsByPk?.medicines ?? [];
  const medicineRecommendations = (
    data?.appointmentsByPk?.medicine_recommendations ?? []
  ).filter((m) => !selectedMedicines.find((s) => s.name === m.name));
  const llmOutputRecommendationId = medicineRecommendations.find(
    (r) => r.recommendationSource === 'LLM Output',
  )?.recommendationSourceId;

  const [addMedication] = useMutation(addAppointmentMedicineMutation, {
    onError: () => toast.error('Failed to accept recommendation'),
    optimisticResponse: (data) => ({
      insertAppointmentMedicinesOne: {
        __typename: 'AppointmentMedicines' as const,
        id: 'optimistic-id',
        name: data.name,
        ingredients: data.ingredients ?? null,
        externalMedicineId: data.externalMedicineId ?? null,
        appointmentId,
        tenantId,
        externalRawData: null,
      },
    }),
    update: (_cache, { data }) => {
      cacheUtils.updateFragment(
        {
          fragment: appointmentMedicinesFragment,
          key: { id: appointmentId },
        },
        (existing) => {
          if (!existing || !data?.insertAppointmentMedicinesOne) {
            return existing;
          }
          return {
            ...existing,
            medicines: [
              ...existing.medicines,
              data.insertAppointmentMedicinesOne,
            ],
          };
        },
      );
    },
  });

  const [removeMedication] = useMutation(removeAppointmentMedicineMutation, {
    onError: () => toast.error('Failed to remove medicine'),
    optimisticResponse: (data) => ({
      deleteAppointmentMedicines: {
        returning: [
          {
            __typename: 'AppointmentMedicines' as const,
            id: data.appointmentMedicineId,
          },
        ],
      },
    }),
    update: (_cache, { data }) => {
      cacheUtils.updateFragment(
        {
          fragment: appointmentMedicinesFragment,
          key: { id: appointmentId },
        },
        (existing) => {
          if (!existing || !data?.deleteAppointmentMedicines) {
            return existing;
          }
          return {
            ...existing,
            medicines: [
              ...existing.medicines.filter(
                (m) =>
                  m.id !== data.deleteAppointmentMedicines?.returning?.[0]?.id,
              ),
            ],
          };
        },
      );
    },
  });

  const onAcceptRecommendation = (
    appointmentMedicineRecommendationId: string,
  ) => {
    if (isReadonly) {
      return;
    }
    const recommendation = medicineRecommendations.find(
      (r) => r.id === appointmentMedicineRecommendationId,
    );
    if (!recommendation) {
      return;
    }

    addMedication({
      variables: {
        appointmentId,
        name: recommendation.name,
        ingredients: recommendation.ingredients,
        externalMedicineId: recommendation.recommendationSourceId,
        rawData: recommendation.externalRawData,
      },
    });
  };

  const onItemAdded = async (medication: Medication) => {
    await addMedication({
      variables: {
        appointmentId,
        name: medication.name,
        ingredients: medication.ingredients,
        externalMedicineId: medication.id,
        rawData: medication.rawData,
      },
    });
  };

  const onItemRemoved = async (medication: Medication) => {
    removeMedication({
      variables: {
        appointmentId,
        appointmentMedicineId: medication.id,
      },
    });
  };

  if (isReadonly && !medicineRecommendations.length) {
    return null;
  }

  return (
    <VStack gap={4}>
      <HStack gap={3}>
        <H4>Medication</H4>
      </HStack>
      <VStack gap={6}>
        <HStack>
          <SearchableMedicines
            disabled={isReadonly}
            onItemAdded={onItemAdded}
            onItemRemoved={onItemRemoved}
            selectedItems={selectedMedicines.map((m) => ({
              id: m.id,
              name: m.name,
              ingredients: m.ingredients ?? undefined,
              externalMedicineId: m.externalMedicineId ?? undefined,
              rawData: m.externalRawData ?? undefined,
            }))}
          />
        </HStack>
        {Boolean(medicineRecommendations.length) && !isReadonly && (
          <VStack gap={3}>
            <h6
              className={legacyTextStyles.body({
                weight: 'semibold',
                size: 's',
                className: 'uppercase text-text-tertiary',
              })}
            >
              Recommended Medication
            </h6>

            <HStack gap={3} wrap>
              {medicineRecommendations.map(({ name, ingredients, id }) => {
                return (
                  <MedicineDetails
                    onClick={() => {
                      onAcceptRecommendation(id);
                    }}
                    startAdornment={
                      <div className="pl-1">
                        <Icon
                          size="xs"
                          name="Plus"
                          className="text-text-tertiary"
                        />
                      </div>
                    }
                    key={id}
                    name={name}
                    ingredients={ingredients ?? ''}
                    type="outlineDashed"
                  />
                );
              })}
            </HStack>
          </VStack>
        )}
      </VStack>
      {llmOutputRecommendationId && (
        <InlineFeedbackBox
          appointmentId={appointmentId}
          llmOutputId={llmOutputRecommendationId}
          sectionLabel={'Were these medications helpful?'}
        />
      )}
    </VStack>
  );
};
